import { ChangeEvent, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useProduct } from 'api/useProduct';
import { ProductsSequence } from 'common/contracts';
import { ImgOption } from 'design-system/AutocompleteInput/AutocompleteInput';
import { transformQuantityValue } from 'utils/formattingUtil';

import AutoCompleteInput from 'components/AutoCompleteInput/AutoCompleteInput';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import Input from 'components/Input/Input';
import type { Product } from 'components/ProductsForm/ProductsForm.types';

import type { TabsProps } from '../types';

interface CardProps extends TabsProps {
  productSequenceItem: ProductsSequence;
  sequenceIndex: number;
}

const MissionCard: React.FC<CardProps> = ({
  formikProps,
  productSequenceItem,
  sequenceIndex
}) => {
  const { setFieldValue, handleBlur, values } = formikProps;
  const { publisherId } = useParams();
  const { data: productsData } = useProduct(publisherId).getProducts;
  const [localQuantities, setLocalQuantities] = useState<
    Record<number, string>
  >({});

  const getProductOptions = useMemo(() => {
    if (!productsData?.result) return [];
    return productsData.result.map((product: Product) => ({
      ...product,
      url: product.images?.[0]?.url,
      name: product.name,
      key: product.name,
      value: product.productId,
      type: product.type,
      publisherProductId: product.publisherProductId
    }));
  }, [productsData?.result]);

  const updateSubOfferData = (updatedData: Partial<ProductsSequence>) => {
    const updatedSequenceItem = {
      ...values.productsSequence[sequenceIndex],
      ...updatedData
    };
    setFieldValue(`productsSequence[${sequenceIndex}]`, updatedSequenceItem);
  };

  const handleProductChange = (selectedOption: any) => {
    if (!selectedOption) {
      updateSubOfferData({ products: [] });
      return;
    }

    const product = {
      productId: selectedOption.productId,
      quantity: productSequenceItem?.products?.[0]?.quantity,
      product: {
        ...selectedOption,
        value: selectedOption.productId,
        key: selectedOption.name,
        url: selectedOption.images?.[0]?.url
      }
    };

    updateSubOfferData({ products: [product] });
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const newOffersQuantity =
      inputValue.length !== 0 ? transformQuantityValue(inputValue) : inputValue;

    if (Number(newOffersQuantity) < 1) return;

    const existingProduct =
      values.productsSequence?.[sequenceIndex]?.products?.[0];

    const updatedProductSequenceItem = {
      products: [
        {
          productId:
            existingProduct?.productId || existingProduct?.product?.value,
          quantity: newOffersQuantity,
          ...(existingProduct?.product
            ? { product: existingProduct.product }
            : {})
        }
      ]
    };

    updateSubOfferData(updatedProductSequenceItem);
    setLocalQuantities((prev) => ({ ...prev, [sequenceIndex]: inputValue }));
  };

  const handleBarSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedProductSequenceItem = { barSize: Number(e.target.value) };
    updateSubOfferData(updatedProductSequenceItem);
  };

  const handleDeleteProductSequence = (index: number) => {
    const updatedSequence = values.productsSequence
      .filter((_: ProductsSequence, i: number) => i !== index)
      .map((item: any, i: number) => ({ ...item, index: i + 1 }));

    setFieldValue('productsSequence', updatedSequence);
  };

  const handleDuplicateProductSequence = (index: number) => {
    const newProductsSequence = [...values.productsSequence];
    const itemToDuplicate = newProductsSequence[index];

    const duplicatedSequence = {
      ...itemToDuplicate,
      index: newProductsSequence.length + 1,
      products: itemToDuplicate.products.map((p: any) => ({ ...p }))
    };

    const updatedSequence = [
      ...newProductsSequence.slice(0, index + 1),
      duplicatedSequence,
      ...newProductsSequence.slice(index + 1)
    ].map((item, i) => ({ ...item, index: i + 1 }));

    setFieldValue('productsSequence', updatedSequence);
  };

  return (
    <div className="flex flex-col p-4 bg-base-white border border-base-border rounded-lg min-w-[528px] w-[530px] mb-6 text-base-foreground">
      <div className="flex justify-between items-center mb-2">
        <p className="text-xl font-semibold">{`Mission ${productSequenceItem.index}`}</p>
        <ActionCell
          rowData={productSequenceItem}
          actions={[
            {
              label: 'Duplicate',
              handler: () => handleDuplicateProductSequence(sequenceIndex)
            },
            {
              label: 'Delete',
              handler: () => handleDeleteProductSequence(sequenceIndex),
              disabled: values.productsSequence?.length === 1
            }
          ]}
        />
      </div>

      <div className="w-1/2 mb-4">
        <Input
          label="Bar size"
          name={`productsSequence[${sequenceIndex}].barSize`}
          type="number"
          value={values.productsSequence[sequenceIndex]?.barSize || 1}
          onChange={handleBarSizeChange}
          onBlur={handleBlur}
          className="w-full rounded-md py-2 text-base-foreground"
        />
      </div>

      <div className="w-[500px] border border-[#ddd] rounded-md">
        <table className="w-full">
          <thead className="text-muted-foreground font-medium text-sm leading-[20px] font-sans h-12">
            <tr>
              <th
                key="name"
                className="px-4 py-2 text-left font-normal text-base-mutedForeground border-b border-r"
              >
                Product
              </th>

              <th
                key="amount"
                className="px-4 py-2 text-left font-normal text-base-mutedForeground border-b"
              >
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="w-1/2 px-4 py-2 text-left text-base-foreground min-w-0 border-r">
                <div className="text-black text-base font-normal leading-normal font-montserrat">
                  <AutoCompleteInput
                    options={getProductOptions || []}
                    onChange={(newValue) => handleProductChange(newValue)}
                    currentOption={getProductOptions.find(
                      (option: ImgOption) =>
                        option.value ===
                        values.productsSequence[sequenceIndex]?.products[0]
                          ?.productId
                    )}
                    placeholder="Search"
                    className="border-none shadow-none focus:ring-0 focus:shadow-none"
                  />
                </div>
              </td>

              <td className="w-1/2 px-4 py-2 text-left text-base-foreground min-w-0 max-w-0">
                <div className="overflow-hidden text-black text-base font-normal leading-normal font-montserrat truncate">
                  <Input
                    name="amount"
                    value={
                      localQuantities[0] ??
                      (productSequenceItem?.products[0]?.quantity || '')
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleQuantityChange(e)
                    }
                    type="number"
                    className="[&_input]:border-none [&_input]:px-0 [&_input:focus]:ring-transparent [&_input:focus]:outline-none"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MissionCard;
